import type { EntityRecordMap, Mutation, SyncChange, SyncEntity } from '@clim/domain';

/**
 * Cycle d'une mutation locale (CDC §23) : pending → syncing → synced (retirée de la file)
 * ou conflict / failed (retirée de la file et signalée dans les alertes).
 */
export type MutationSyncState = 'pending' | 'syncing';

export interface OutboxEntry {
  /** monotonic local order */
  seq: number;
  mutation: Mutation;
  createdAt: string;
  attempts: number;
  state?: MutationSyncState;
  lastError?: string;
}

export interface SyncIssue {
  id: string;
  mutationId: string;
  type: Mutation['type'];
  entityId: string;
  /** failed = refusée par le serveur ; conflict = appliquée en conservant des valeurs serveur */
  kind: 'failed' | 'conflict';
  message: string;
  details?: unknown;
  at: string;
}

export interface SyncMeta {
  cursor: string;
  lastSyncAt: string | null;
  issues: SyncIssue[];
  nextSeq: number;
  /** secrets of the portal links created on THIS device (never sent to the server) */
  portalTokens?: Record<string, string>;
}

export type RecordsState = { [E in SyncEntity]: Record<string, EntityRecordMap[E]> };

export interface PersistedState {
  records: RecordsState;
  outbox: OutboxEntry[];
  meta: SyncMeta;
}

/**
 * Persistence port of the sync engine. Implementations: in-memory (tests), IndexedDB (web),
 * SQLite (mobile). Every method must be atomic: a crash can never leave a pulled page
 * applied without its cursor (or the reverse).
 */
export interface SyncStorage {
  load(): Promise<PersistedState | null>;
  /** applies server changes and the new cursor atomically; `reset` wipes server records first */
  applyPull(changes: SyncChange[], meta: SyncMeta, reset: boolean): Promise<void>;
  addOutbox(entry: OutboxEntry, meta: SyncMeta): Promise<void>;
  /** removes acknowledged entries, updates the others and the meta atomically */
  ackOutbox(removed: number[], updated: OutboxEntry[], meta: SyncMeta): Promise<void>;
  setMeta(meta: SyncMeta): Promise<void>;
  clear(): Promise<void>;
}

export function emptyRecords(): RecordsState {
  return {
    territory: {},
    dossier_type: {},
    reason: {},
    client: {},
    dossier: {},
    comment: {},
    catalog_section: {},
    catalog_family: {},
    catalog_brand: {},
    catalog_item: {},
    dossier_line: {},
    dossier_file: {},
    legal_text: {},
    portal_link: {},
    notification: {},
    announcement: {},
    resource: {},
    prospect: {},
    intervention: {},
    payment_method: {},
    payment: {},
    commission: {},
    sav: {},
  };
}

export function emptyMeta(): SyncMeta {
  return { cursor: '', lastSyncAt: null, issues: [], nextSeq: 1 };
}

/** Reference implementation, also used by unit tests. */
export class MemoryStorage implements SyncStorage {
  state: PersistedState | null = null;

  async load() {
    return this.state ? structuredClone(this.state) : null;
  }
  private ensure(): PersistedState {
    this.state ??= { records: emptyRecords(), outbox: [], meta: emptyMeta() };
    return this.state;
  }
  async applyPull(changes: SyncChange[], meta: SyncMeta, reset: boolean) {
    const s = this.ensure();
    if (reset) s.records = emptyRecords();
    applyChanges(s.records, changes);
    s.meta = structuredClone(meta);
  }
  async addOutbox(entry: OutboxEntry, meta: SyncMeta) {
    const s = this.ensure();
    s.outbox.push(structuredClone(entry));
    s.meta = structuredClone(meta);
  }
  async ackOutbox(removed: number[], updated: OutboxEntry[], meta: SyncMeta) {
    const s = this.ensure();
    s.outbox = s.outbox
      .filter((e) => !removed.includes(e.seq))
      .map((e) => structuredClone(updated.find((u) => u.seq === e.seq) ?? e));
    s.meta = structuredClone(meta);
  }
  async setMeta(meta: SyncMeta) {
    this.ensure().meta = structuredClone(meta);
  }
  async clear() {
    this.state = null;
  }
}

export function applyChanges(records: RecordsState, changes: SyncChange[]) {
  for (const c of changes) {
    const bucket = records[c.entity] as Record<string, unknown>;
    if (c.op === 'upsert' && c.record) bucket[c.id] = c.record;
    else delete bucket[c.id];
  }
}

/**
 * Local binary content of files not yet received by the server (photos taken offline…).
 * Implementations: memory (tests), IndexedDB (web), file system (mobile).
 * Content is removed once the server has it: the device keeps no copy of private files.
 */
export interface BlobStore {
  put(id: string, data: Uint8Array): Promise<void>;
  /** bytes [start, end) — null if unknown */
  read(id: string, start?: number, end?: number): Promise<Uint8Array | null>;
  delete(id: string): Promise<void>;
  list(): Promise<string[]>;
}

export class MemoryBlobStore implements BlobStore {
  readonly blobs = new Map<string, Uint8Array>();
  async put(id: string, data: Uint8Array) {
    this.blobs.set(id, data.slice());
  }
  async read(id: string, start = 0, end?: number) {
    const b = this.blobs.get(id);
    return b ? b.slice(start, end) : null;
  }
  async delete(id: string) {
    this.blobs.delete(id);
  }
  async list() {
    return [...this.blobs.keys()];
  }
}
